import { sanitizeResponseBody, sanitizeText, sanitizeUrl } from './format-diagnostic'
import type { DiagnosticAction, DiagnosticEvent } from './diagnostic-types'

export type IssueBusinessContext = {
  systemCode?: string
  menuTrail?: string[]
  operations?: string[]
  userAccount?: string
  roles?: string[]
}

export type AiIssuePackage = {
  schemaVersion: 1
  incident: {
    kind: DiagnosticEvent['kind']
    at: string
    route: string
    build: DiagnosticEvent['build']
    correlation: DiagnosticEvent['correlation']
    error: DiagnosticEvent['error']
  }
  reproduction: { actions: DiagnosticAction[] }
  businessContext?: IssueBusinessContext
  request?: DiagnosticEvent['request']
  redaction: { applied: true; removed: string[] }
}

type BuildIssuePackageInput = {
  event: DiagnosticEvent
  actions: DiagnosticAction[]
  businessContext?: IssueBusinessContext
}

function sanitizeAction(action: DiagnosticAction): DiagnosticAction {
  return {
    ...action,
    label: action.label ? sanitizeText(action.label) : undefined,
    element: action.element
      ? {
          ...action.element,
          testId: action.element.testId ? sanitizeText(action.element.testId) : undefined,
          dataComponent: action.element.dataComponent ? sanitizeText(action.element.dataComponent) : undefined,
          role: action.element.role ? sanitizeText(action.element.role) : undefined,
        }
      : undefined,
  }
}

function maskUserAccount(userAccount: string): string {
  if (userAccount.includes('*')) return sanitizeText(userAccount)
  return `${userAccount.slice(0, 2) || '*'}***`
}

function sanitizeBusinessContext(context: IssueBusinessContext | undefined): IssueBusinessContext | undefined {
  if (!context) return undefined

  const next = {
    systemCode: context.systemCode ? sanitizeText(context.systemCode) : undefined,
    menuTrail: context.menuTrail?.map(sanitizeText),
    operations: context.operations?.map(sanitizeText),
    userAccount: context.userAccount ? maskUserAccount(context.userAccount) : undefined,
    roles: context.roles?.map(sanitizeText),
  }

  return Object.values(next).some((value) => value && (!Array.isArray(value) || value.length > 0)) ? next : undefined
}

function sanitizeRequest(request: DiagnosticEvent['request']): DiagnosticEvent['request'] {
  if (!request) return undefined

  return {
    ...request,
    method: sanitizeText(request.method),
    path: sanitizeUrl(request.path),
    responseCode: request.responseCode ? sanitizeText(request.responseCode) : undefined,
    responseMessage: request.responseMessage ? sanitizeText(request.responseMessage) : undefined,
    responseBody: typeof request.responseBody === 'undefined' ? undefined : sanitizeResponseBody(request.responseBody),
    traceId: request.traceId ? sanitizeText(request.traceId) : undefined,
    input: request.input
      ? {
          queryKeys: request.input.queryKeys?.map(sanitizeText),
          body: request.input.body?.map(sanitizeText),
        }
      : undefined,
  }
}

export function buildIssuePackage({ event, actions, businessContext }: BuildIssuePackageInput): AiIssuePackage {
  return {
    schemaVersion: 1,
    incident: {
      kind: event.kind,
      at: new Date(event.at).toISOString(),
      route: sanitizeUrl(event.pageUrl),
      build: { commit: sanitizeText(event.build.commit), mode: sanitizeText(event.build.mode) },
      correlation: event.correlation,
      error: {
        name: sanitizeText(event.error.name),
        message: sanitizeText(event.error.message),
        stack: event.error.stack ? sanitizeText(event.error.stack) : undefined,
      },
    },
    reproduction: { actions: actions.slice().reverse().map(sanitizeAction) },
    businessContext: sanitizeBusinessContext(businessContext),
    request: sanitizeRequest(event.request),
    redaction: { applied: true, removed: ['credentials', 'sensitive values'] },
  }
}

function formatAction(action: DiagnosticAction): string {
  const label = action.label ? `「${action.label}」` : '未命名元素'
  const component = action.element?.dataComponent ? ` [${action.element.dataComponent}]` : ''
  return `- ${new Date(action.at).toISOString()} ${action.type === 'click' ? '点击' : '按键'} ${label}${component}`
}

export function formatIssuePackageMarkdown(issuePackage: AiIssuePackage): string {
  const { incident, reproduction, businessContext, request } = issuePackage
  const lines = [
    '# AI Issue Package',
    '',
    '## 故障现场',
    `- 时间：${incident.at}`,
    `- 页面：${incident.route}`,
    `- 构建：${incident.build.commit} (${incident.build.mode})`,
    `- 故障类型：${incident.kind === 'request' ? '请求失败' : '运行时异常'}`,
    `- 关联方式：${incident.correlation}`,
  ]

  if (reproduction.actions.length > 0) {
    lines.push('', '## 复现操作', ...reproduction.actions.map(formatAction))
  }

  if (businessContext) {
    lines.push('', '## 业务上下文')
    if (businessContext.systemCode) lines.push(`- 系统：${businessContext.systemCode}`)
    if (businessContext.menuTrail?.length) lines.push(`- 菜单：${businessContext.menuTrail.join(' / ')}`)
    if (businessContext.operations?.length) lines.push(`- 当前操作：${businessContext.operations.join(', ')}`)
    if (businessContext.userAccount) lines.push(`- 用户：${businessContext.userAccount}`)
    if (businessContext.roles?.length) lines.push(`- 角色：${businessContext.roles.join(', ')}`)
  }

  if (request) {
    lines.push('', '## 失败请求', `- 客户端：${request.client}`, `- ${request.method} ${request.path}`)
    lines.push(`- 状态：${request.status ?? '未知'}，耗时：${request.durationMs ?? '未知'}ms`)
    if (request.traceId) lines.push(`- traceId：${request.traceId}`)
    if (request.input?.queryKeys?.length || request.input?.body?.length) {
      const input = [
        request.input.queryKeys?.length ? `查询参数：${request.input.queryKeys.join(', ')}` : undefined,
        request.input.body?.length ? `请求体：${request.input.body.join(', ')}` : undefined,
      ].filter((value): value is string => Boolean(value))
      lines.push(`- 请求输入：${input.join('；')}`)
    }
    if (typeof request.responseBody !== 'undefined') {
      lines.push('', '## 服务端响应', '```json', JSON.stringify(request.responseBody, null, 2), '```')
    } else if (request.responseCode || request.responseMessage) {
      lines.push(`- 服务端：${[request.responseCode, request.responseMessage].filter(Boolean).join('，')}`)
    }
  }

  lines.push('', '## 前端错误', `- ${incident.error.name}：${incident.error.message}`)
  if (incident.error.stack) lines.push(`- 堆栈：${incident.error.stack}`)
  lines.push('', '## 数据处理', '- 已移除凭证与敏感字段，并截断过长数据')
  return lines.join('\n')
}

export function formatIssuePackageJson(issuePackage: AiIssuePackage): string {
  return JSON.stringify(issuePackage, null, 2)
}
